import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import { ContentStatus } from "~/lib/schemas/db-enums-cih";
import type { JsonValue } from "~/lib/shared/json";

// ============================================================================
// Types
// ============================================================================

export interface AdminCtpRow {
  id: string;
  name: string;
  slug: string | null;
  website: string | null;
  logoUrl: string | null;
  description: string | null;
  deliveryFormat: string | null;
  accreditationLevel: string | null;
  duration: string | null;
  tuitionRange: string | null;
  body: JsonValue | null;
  prerequisites: string | null;
  icfHours: string | null;
  faqs: { question: string; answer: string }[] | null;
  ccoId: string;
  ccoName: string;
  ccoSlug: string | null;
  /** True when the public program page exists (published, not a draft copy). */
  isPublished: boolean;
  coachCount: number;
}

export interface CcoDropdownOption {
  id: string;
  name: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export const adminCtpQueryKeys = {
  all: ["admin-ctps"] as const,
  ccoDropdown: ["admin-ctps", "cco-dropdown"] as const,
};

// ============================================================================
// Server Functions
// ============================================================================

/**
 * List all CTPs with coach count and parent CCO name/slug, ordered by name.
 */
export const getAdminCtpsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .handler(async (): Promise<AdminCtpRow[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { ctps, coachesCtps, ccos } = await import("~/../drizzle/schema");
    const { eq, asc, count, sql } = await import("drizzle-orm");

    const db = await getDBWithInit();

    const rows = await db
      .select({
        id: ctps.id,
        name: ctps.name,
        slug: ctps.slug,
        website: ctps.website,
        logoUrl: ctps.logoUrl,
        description: ctps.description,
        deliveryFormat: ctps.deliveryFormat,
        accreditationLevel: ctps.accreditationLevel,
        duration: ctps.duration,
        tuitionRange: ctps.tuitionRange,
        body: ctps.body,
        prerequisites: ctps.prerequisites,
        icfHours: ctps.icfHours,
        faqs: ctps.faqs,
        ccoId: ctps.ccoId,
        ccoName: ccos.name,
        ccoSlug: ccos.slug,
        isPublished: sql<boolean>`(${ctps.status} = ${ContentStatus.Published} and ${ctps.draftOf} is null)`,
        coachCount: count(coachesCtps.coachId),
      })
      .from(ctps)
      .innerJoin(ccos, eq(ctps.ccoId, ccos.id))
      .leftJoin(coachesCtps, eq(ctps.id, coachesCtps.ctpId))
      .groupBy(ctps.id, ccos.name, ccos.slug)
      .orderBy(asc(ctps.name));

    return rows.map((r) => ({ ...r, coachCount: Number(r.coachCount) }));
  });

export const adminCtpsQueryOptions = queryOptions({
  queryKey: adminCtpQueryKeys.all,
  queryFn: () => getAdminCtpsFn(),
});

/**
 * Lightweight CCO list for dropdown selectors.
 */
export const getCcoDropdownOptionsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:read")])
  .handler(async (): Promise<CcoDropdownOption[]> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { ccos } = await import("~/../drizzle/schema");
    const { asc } = await import("drizzle-orm");

    const db = await getDBWithInit();

    return db
      .select({ id: ccos.id, name: ccos.name })
      .from(ccos)
      .orderBy(asc(ccos.name));
  });

export const ccoDropdownQueryOptions = queryOptions({
  queryKey: adminCtpQueryKeys.ccoDropdown,
  queryFn: () => getCcoDropdownOptionsFn(),
});

/**
 * Create a new CTP.
 */
export const createCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      name: z.string().min(1),
      slug: z.string().min(1),
      ccoId: z.string().uuid(),
      website: z.string().nullable().optional(),
      logoUrl: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      deliveryFormat: z.string().optional(),
      accreditationLevel: z.string().optional(),
      duration: z.string().optional(),
      tuitionRange: z.string().optional(),
      body: z
        .string()
        .optional()
        .transform((v) => (v ? JSON.parse(v) : null)),
      prerequisites: z.string().optional(),
      icfHours: z.string().optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { ctps } = await import("~/../drizzle/schema");

    const db = await getDBWithInit();

    await db.insert(ctps).values({
      name: data.name,
      slug: data.slug,
      ccoId: data.ccoId,
      website: data.website ?? null,
      logoUrl: data.logoUrl ?? null,
      description: data.description ?? null,
      deliveryFormat: data.deliveryFormat ?? null,
      accreditationLevel: data.accreditationLevel ?? null,
      duration: data.duration ?? null,
      tuitionRange: data.tuitionRange ?? null,
      body: data.body,
      prerequisites: data.prerequisites ?? null,
      icfHours: data.icfHours ?? null,
    });

    return { success: true };
  });

/**
 * Update an existing CTP (partial update).
 */
export const updateCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
      name: z.string().min(1).optional(),
      slug: z.string().min(1).optional(),
      ccoId: z.string().uuid().optional(),
      website: z.string().nullable().optional(),
      logoUrl: z.string().nullable().optional(),
      description: z.string().nullable().optional(),
      deliveryFormat: z.string().nullable().optional(),
      accreditationLevel: z.string().nullable().optional(),
      duration: z.string().nullable().optional(),
      tuitionRange: z.string().nullable().optional(),
      body: z
        .string()
        .nullable()
        .optional()
        .transform((v) => (typeof v === "string" ? JSON.parse(v) : v)),
      prerequisites: z.string().nullable().optional(),
      icfHours: z.string().nullable().optional(),
      faqs: z
        .array(z.object({ question: z.string(), answer: z.string() }))
        .optional(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    // Delegates rather than writing `ctps` itself. This wrapper used to issue
    // its own UPDATE, which meant the one caller that can still repoint a
    // program's owner from this screen skipped the delivering-school /
    // accreditor invariant that `updateCtp` enforces under a row lock — and
    // that invariant is what lets the organization counts sum delivered and
    // accredited without double-counting.
    const { updateCtp } = await import("~/lib/server/cih/ctps");
    return updateCtp(data);
  });

/**
 * Delete a CTP. Coach joins cascade via coachesCtps.
 */
export const deleteCtpFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("cih:write")])
  .validator(
    z.object({
      id: z.string().uuid(),
    }),
  )
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const { getDBWithInit } = await import("~/lib/db");
    const { ctps } = await import("~/../drizzle/schema");
    const { eq } = await import("drizzle-orm");

    const db = await getDBWithInit();

    await db.delete(ctps).where(eq(ctps.id, data.id));

    return { success: true };
  });
